import { ORDER_STATUS } from '../order/order.interface';
import type { OrderStatus } from '../order/order.interface';
import type { OrderKpiMetrics } from './kpi.interface';

/**
 * Status -> metric group mapping for the `order-kpis` KPI. The 5 groups below partition every
 * `ORDER_STATUS` value the pipeline counts (`ORDER_KPI_COUNTED_STATUSES`), excluding only
 * `ORDER_STATUS.CANCELLED` — see `order-kpis.pipeline.spec.ts` in `pharos-back-deliveries` for the
 * coverage guard (lib-types has no test infrastructure). `completedOnTime`/`completedLate` are not
 * status groups — they are a time-based split of the `completed` group, computed from
 * `completedAt` vs. `requestedTimeWindow.end` in the pipeline itself.
 */
export const ORDER_KPI_STATUS_GROUPS = {
  anomaly: [ORDER_STATUS.ANOMALY],
  completed: [ORDER_STATUS.COMPLETED],
  failed: [ORDER_STATUS.FAILED],
  inprogress: [
    ORDER_STATUS.CARRIER_ASSIGNED,
    ORDER_STATUS.CARRIER_ACCEPTED,
    ORDER_STATUS.DRIVER_ASSIGNED,
    ORDER_STATUS.DRIVER_ACCEPTED,
    ORDER_STATUS.DRIVING_TO_PICKUP,
    ORDER_STATUS.ARRIVED_AT_PICKUP,
    ORDER_STATUS.WAITING_FOR_PICKUP,
    ORDER_STATUS.PICKUP_COMPLETED,
    ORDER_STATUS.IN_TRANSIT,
    ORDER_STATUS.DRIVING_TO_DELIVERY,
    ORDER_STATUS.ARRIVED_AT_DELIVERY,
  ],
  unassigned: [ORDER_STATUS.DRAFT, ORDER_STATUS.NEW, ORDER_STATUS.PENDING, ORDER_STATUS.CARRIER_REJECTED],
} satisfies Record<
  Exclude<keyof OrderKpiMetrics, 'completedLate' | 'completedOnTime' | 'total'>,
  readonly OrderStatus[]
>;

/** Never counted toward any `order-kpis` metric, including `total`. */
export const ORDER_KPI_EXCLUDED_STATUSES: readonly OrderStatus[] = [ORDER_STATUS.CANCELLED];

/** Flattened union of the 5 `ORDER_KPI_STATUS_GROUPS` — every status counted toward `total`. */
export const ORDER_KPI_COUNTED_STATUSES: readonly OrderStatus[] = [
  ...ORDER_KPI_STATUS_GROUPS.anomaly,
  ...ORDER_KPI_STATUS_GROUPS.unassigned,
  ...ORDER_KPI_STATUS_GROUPS.inprogress,
  ...ORDER_KPI_STATUS_GROUPS.completed,
  ...ORDER_KPI_STATUS_GROUPS.failed,
];

/** All `OrderKpiMetrics` keys — shared between the backend pipeline and the frontend metric narrowing. */
export const ORDER_KPI_METRIC_KEYS: readonly (keyof OrderKpiMetrics)[] = [
  'anomaly',
  'completed',
  'completedLate',
  'completedOnTime',
  'failed',
  'inprogress',
  'total',
  'unassigned',
];
